import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { HelperText, IconButton, Text, TextInput } from 'react-native-paper';

import { AddIcon, CloseIcon } from '@/assets';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { useFocusRing } from '@/shared/hooks/useFocusRing';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export const MAX_FACTS = 5;

export type FactEntry = {
  key: string;
  name: string;
  value: string;
};

type Props = {
  facts: FactEntry[];
  onChange: (next: FactEntry[]) => void;
  disabled?: boolean;
};

const emptyFact = (): FactEntry => ({
  key: `${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
  name: '',
  value: ''
});

export const FactList: FC<Props> = ({ facts, onChange, disabled }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const focusRing = useFocusRing();

  const canAddMore = facts.length < MAX_FACTS;

  const handleUpdate = useCallback(
    (key: string, patch: Partial<Omit<FactEntry, 'key'>>) => {
      onChange(
        facts.map((entry) =>
          entry.key === key ? { ...entry, ...patch } : entry
        )
      );
    },
    [facts, onChange]
  );

  const handleAdd = useCallback(() => {
    onChange([...facts, emptyFact()]);
  }, [facts, onChange]);

  const handleRemove = useCallback(
    (key: string) => {
      onChange(facts.filter((entry) => entry.key !== key));
    },
    [facts, onChange]
  );

  return (
    <View style={styles.wrapper}>
      <Text variant="titleMedium" style={styles.title}>
        {t('sections.facts', { count: facts.length, max: MAX_FACTS })}
      </Text>
      <Text variant="bodyMedium" style={styles.description}>
        {t('sections.factsDescription')}
      </Text>

      {facts.map((entry) => {
        const isIncomplete =
          (!isEmptyOrNull(entry.name) || !isEmptyOrNull(entry.value)) &&
          (isEmptyOrNull(entry.name) || isEmptyOrNull(entry.value));

        return (
          <View key={entry.key} style={styles.cardWrapper}>
            <View style={styles.card}>
              <TextInput
                accessibilityLabel={t('fields.factName')}
                accessibilityHint={t('fields.factName')}
                mode="outlined"
                label={t('fields.factName')}
                placeholder={t('placeholders.factName')}
                value={entry.name}
                onChangeText={(text) => handleUpdate(entry.key, { name: text })}
                disabled={disabled}
                error={isIncomplete && isEmptyOrNull(entry.name)}
                style={styles.input}
              />
              <TextInput
                accessibilityLabel={t('fields.factValue')}
                accessibilityHint={t('fields.factValue')}
                mode="outlined"
                label={t('fields.factValue')}
                placeholder={t('placeholders.factValue')}
                value={entry.value}
                onChangeText={(text) =>
                  handleUpdate(entry.key, { value: text })
                }
                disabled={disabled}
                error={isIncomplete && isEmptyOrNull(entry.value)}
                style={styles.input}
              />
              {isIncomplete && (
                <HelperText type="error" visible>
                  {t('errors.factIncomplete')}
                </HelperText>
              )}
            </View>
            <IconButton
              mode="contained"
              // Match the MediaImageSized remove button: dark scrim-style
              // contained button in the top-right corner.
              containerColor={theme.colors.inverseSurface}
              icon={({ size }) => (
                <CloseIcon
                  fill={theme.colors.inverseOnSurface}
                  width={size}
                  height={size}
                />
              )}
              size={20}
              onPress={() => handleRemove(entry.key)}
              disabled={disabled}
              accessibilityLabel={t('actions.removeFact')}
              accessibilityHint={t('actions.removeFact')}
              style={[styles.removeButton, focusRing.ringStyle]}
              {...focusRing.focusProps}
            />
          </View>
        );
      })}

      <OutlinedButton
        icon={({ size, color }) => (
          <AddIcon fill={color} width={size} height={size} />
        )}
        onPress={handleAdd}
        disabled={disabled || !canAddMore}
        style={styles.addButton}
      >
        {t('actions.addFact')}
      </OutlinedButton>
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    wrapper: { gap: 8 },
    title: { color: theme.colors.onSurface },
    description: { color: theme.colors.onSurfaceVariant },
    cardWrapper: {
      // Leave room at the top so the overlapping close button isn't clipped.
      paddingTop: 8,
      marginTop: 8
    },
    card: {
      gap: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.outlineVariant,
      borderRadius: 16,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    input: { backgroundColor: theme.colors.surfaceContainerLowest },
    removeButton: {
      position: 'absolute',
      top: -6,
      right: -6,
      margin: 0
    },
    addButton: { alignSelf: 'flex-start', marginTop: 8, borderRadius: 24 }
  });
